import AnalyticsIcon from '@material-design-icons/svg/filled/analytics.svg?react';
import AppsIcon from '@material-design-icons/svg/filled/apps.svg?react';
import AutoAwesomeIcon from '@material-design-icons/svg/filled/auto_awesome.svg?react';
import GroupIcon from '@material-design-icons/svg/filled/group.svg?react';
import NotificationsIcon from '@material-design-icons/svg/filled/notifications.svg?react';
import SupportAgentIcon from '@material-design-icons/svg/filled/support_agent.svg?react';
import type { Dapp } from '@src/dto/Dapp';
import { motion } from 'framer-motion';
import type { ComponentProps } from 'react';
import { Link } from 'react-router';
import tokenSkrIcon from '../../assets/skr-logo.svg';
import { useUnreadCount } from '../../contexts/AlertContext';
import type { UserRole } from '../../utils/roleUtils';
import { NavButton } from '../NavButton';
import { AddDappButton } from './AddDappButton';
import { DappsSection } from './DappsSection';
import {
  KybStatusSection,
  type SidebarVerificationCard,
} from './KybStatusSection';
import {
  DappsSectionSkeleton,
  KybStatusSkeleton,
  SidebarHeaderSkeleton,
} from './skeletons';
import { NavButtonSkeleton } from './skeletons/NavButtonSkeleton';
import { SidebarHeader, type SidebarHeaderProps } from './SidebarHeader';
import { SidebarWalletConnector } from './SidebarWalletConnector';
import {
  getSidebarUtilityNavItems,
  type SidebarUtilityNavItem,
} from './utilityNav';

interface SidebarMainContentProps {
  isLoadingData: boolean;
  organizationName: string;
  publisherAvatarUrl: string | null;
  isDropdownOpen: boolean;
  activePublisherUserJoinId?: string | null;
  accessiblePublishers: SidebarHeaderProps['accessiblePublishers'];
  isSwitchingPublisher: boolean;
  onDropdownToggle: () => void;
  onSwitchPublisher: SidebarHeaderProps['onSwitchPublisher'];
  onSettings: () => void;
  onSignOut: () => void;
  hasPublisherProfile: boolean;
  kybStatus: SidebarVerificationCard;
  isActiveRoute: (path: string) => boolean;
  canAccessSkrClaim: boolean;
  userRole: UserRole | null | undefined;
  userEmail?: string | null;
  currentPath: string;
  onLinkClick?: () => void;
  primaryDapps: Dapp[];
  expandedDappId: string | null;
  onDappToggle: (dappId: string | null) => void;
  isAddDappDropdownOpen: boolean;
  onToggleAddDappDropdown: () => void;
  onImportDapp: () => void;
  isWalletConnected: boolean;
  isPublisherFrozen: boolean;
  isPublisherVerificationApproved: boolean;
  publisherVerificationMessage: string;
}

export function SidebarMainContent({
  isLoadingData,
  organizationName,
  publisherAvatarUrl,
  isDropdownOpen,
  activePublisherUserJoinId,
  accessiblePublishers,
  isSwitchingPublisher,
  onDropdownToggle,
  onSwitchPublisher,
  onSettings,
  onSignOut,
  hasPublisherProfile,
  kybStatus,
  isActiveRoute,
  canAccessSkrClaim,
  userRole,
  userEmail,
  currentPath,
  onLinkClick,
  primaryDapps,
  expandedDappId,
  onDappToggle,
  isAddDappDropdownOpen,
  onToggleAddDappDropdown,
  onImportDapp,
  isWalletConnected,
  isPublisherFrozen,
  isPublisherVerificationApproved,
  publisherVerificationMessage,
}: SidebarMainContentProps) {
  return (
    <>
      <div className="flex flex-col justify-start items-start flex-1 min-h-0">
        <div className="pt-2 pb-4 flex flex-col justify-center items-center w-full flex-shrink-0">
          <div data-dropdown="user">
            {isLoadingData ? (
              <SidebarHeaderSkeleton />
            ) : (
              <SidebarHeader
                organizationName={organizationName}
                publisherAvatarUrl={publisherAvatarUrl}
                isDropdownOpen={isDropdownOpen}
                activePublisherUserJoinId={activePublisherUserJoinId}
                accessiblePublishers={accessiblePublishers}
                isSwitchingPublisher={isSwitchingPublisher}
                onDropdownToggle={onDropdownToggle}
                onSwitchPublisher={onSwitchPublisher}
                onSettings={onSettings}
                onSignOut={onSignOut}
              />
            )}
          </div>
        </div>

        <div className="flex-shrink-0 mb-1">
          <SidebarWalletConnector />
        </div>

        <div className="flex flex-col w-full items-center px-2 pb-3 flex-shrink-0">
          {hasPublisherProfile && (
            <div className="mb-1">
              {isLoadingData ? (
                <KybStatusSkeleton />
              ) : (
                <KybStatusSection
                  kybStatus={kybStatus}
                  isActiveRoute={isActiveRoute}
                />
              )}
            </div>
          )}

          <div className="flex flex-col gap-y-1 w-full items-center">
            {canAccessSkrClaim && (
              <SkrClaimNavItem
                isActive={isActiveRoute('/dashboard/claim')}
                onLinkClick={onLinkClick}
              />
            )}
            <SidebarUtilityNav
              isLoadingData={isLoadingData}
              userRole={userRole}
              userEmail={userEmail}
              isActiveRoute={isActiveRoute}
              currentPath={currentPath}
              onLinkClick={onLinkClick}
            />
          </div>
        </div>

        <div className="h-0 outline-1 outline-offset-[-0.50px] outline-neutral-800 w-full flex-shrink-0"></div>

        <div className="flex-1 min-h-0 w-full overflow-y-auto">
          {isLoadingData ? (
            hasPublisherProfile && <DappsSectionSkeleton />
          ) : (
            <>
              {primaryDapps.length > 0 && (
                <DappsSection
                  dapps={primaryDapps}
                  expandedDappId={expandedDappId}
                  onDappToggle={onDappToggle}
                  isActiveRoute={isActiveRoute}
                  onLinkClick={onLinkClick}
                />
              )}
            </>
          )}
        </div>
      </div>

      {hasPublisherProfile && (
        <div data-dropdown="add-dapp">
          <AddDappButton
            isAddDappDropdownOpen={isAddDappDropdownOpen}
            onToggleDropdown={onToggleAddDappDropdown}
            onImportDapp={onImportDapp}
            isWalletConnected={isWalletConnected}
            isPublisherFrozen={isPublisherFrozen}
            isPublisherVerificationApproved={isPublisherVerificationApproved}
            publisherVerificationMessage={publisherVerificationMessage}
          />
        </div>
      )}
    </>
  );
}

const AlertsNavButton = (props: ComponentProps<typeof NavButton>) => {
  const unreadCount = useUnreadCount();

  return (
    <NavButton
      {...props}
      badge={
        unreadCount > 0 ? (
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: 'spring', stiffness: 500, damping: 15 }}
          >
            <span className="inline-flex h-6 min-w-6 items-center justify-center gap-1.5 rounded-md border border-transparent px-2.5 text-xs font-medium text-[var(--color-warning-accent,var(--color-yellow-500))]">
              <span className="size-1.5 rounded-full bg-[currentColor] opacity-75" />
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          </motion.div>
        ) : undefined
      }
    />
  );
};

interface SkrClaimNavItemProps {
  isActive: boolean;
  onLinkClick?: () => void;
}

function SkrClaimNavItem({ isActive, onLinkClick }: SkrClaimNavItemProps) {
  return (
    <Link to="/dashboard/claim" className="group" onClick={onLinkClick}>
      <div
        className={`w-60 h-9 px-2 py-5 rounded-xl inline-flex justify-start items-center gap-2 transition-all duration-300 ease-out cursor-pointer ${
          isActive
            ? 'bg-[#151717] shadow-[0px_0.375px_0.375px_0px_inset_rgba(255,255,255,0.17)]'
            : ''
        } hover:bg-[#151717] hover:shadow-[0px_0.375px_0.375px_0px_inset_rgba(255,255,255,0.17)]`}
      >
        <div className="flex justify-start items-center gap-2.5">
          <img src={tokenSkrIcon} alt="SKR" className="size-5 rounded-full" />
          <span className="text-sm font-medium bg-[linear-gradient(90deg,#96A3A3_0%,#96A3A3_40%,white_50%,#96A3A3_60%,#96A3A3_100%)] bg-size-[200%_100%] bg-clip-text text-transparent animate-shimmer">
            Claim Your SKR
          </span>
        </div>
      </div>
    </Link>
  );
}

interface SidebarUtilityNavProps {
  isLoadingData: boolean;
  userRole: UserRole | null | undefined;
  userEmail?: string | null;
  isActiveRoute: (path: string) => boolean;
  currentPath: string;
  onLinkClick?: () => void;
}

function SidebarUtilityNav({
  isLoadingData,
  userRole,
  userEmail,
  isActiveRoute,
  currentPath,
  onLinkClick,
}: SidebarUtilityNavProps) {
  if (isLoadingData) {
    return <NavButtonSkeleton width={50} />;
  }

  const utilityNavItems = getSidebarUtilityNavItems(userRole, userEmail);
  const alertsItems = utilityNavItems.filter((item) => item.id === 'alerts');
  const adminItems = utilityNavItems.filter((item) => item.id !== 'alerts');

  return (
    <>
      {alertsItems.map((item) =>
        renderUtilityNavItem(item, isActiveRoute, currentPath, onLinkClick),
      )}
      {adminItems.length > 0 && (
        <>
          <div className="-mx-2 mt-2 h-0 outline-1 outline-offset-[-0.50px] outline-neutral-800 w-[calc(100%+1rem)] flex-shrink-0"></div>
          <div className="w-full pt-3 pb-1 px-2">
            <div className="opacity-70 text-white text-xs font-semibold uppercase leading-tight tracking-tight">
              Admin
            </div>
          </div>
        </>
      )}
      {adminItems.map((item) =>
        renderUtilityNavItem(item, isActiveRoute, currentPath, onLinkClick),
      )}
    </>
  );
}

function renderUtilityNavItem(
  item: SidebarUtilityNavItem,
  isActiveRoute: (path: string) => boolean,
  currentPath: string,
  onLinkClick?: () => void,
) {
  if (item.id === 'alerts') {
    return (
      <AlertsNavButton
        key={item.id}
        to={item.to}
        icon={<NotificationsIcon />}
        label={item.label}
        isActive={isActiveRoute(item.to)}
        currentPath={currentPath}
        onClick={onLinkClick}
      />
    );
  }

  const iconMap = {
    dapps: <AppsIcon />,
    publishers: <GroupIcon />,
    tickets: <SupportAgentIcon />,
    reporting: <AnalyticsIcon />,
    aiSummaries: <AutoAwesomeIcon />,
  };

  return (
    <NavButton
      key={item.id}
      to={item.to}
      icon={iconMap[item.id] ?? <AnalyticsIcon />}
      label={item.label}
      isActive={isActiveRoute(item.to)}
      currentPath={currentPath}
      onClick={onLinkClick}
    />
  );
}
